"use client";

import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type Ref,
  type RefObject,
} from "react";
import type { ChatShareProvider } from "@/src/lib/chat-share/types";
import { defaultPreviewSurfaceBackground } from "@/src/lib/pdf/print-document";
import type { SamplePreviewId } from "@/src/lib/pdf/sample-preview-registry";
import type { EditorPreferencesSource } from "@/src/lib/storage/user-preferences";
import { getEmbeddedSample } from "../editor/embedded-samples";
import type { EditorWorkspaceHandle } from "../editor-workspace";
import { classNames } from "../ui/class-names";
import { embeddedConverterShellClassName } from "./embedded-converter-shell";

/** Start loading before the converter is on-screen so scroll feels instant. */
const converterRootMargin = "480px 0px";

type EditorWorkspaceComponent = ComponentType<
  Readonly<{
    initialMarkdown?: string;
    initialFilename?: string;
    variant?: "embedded" | "fullscreen";
    editorSource?: EditorPreferencesSource;
    chatShareProvider?: ChatShareProvider;
    chatShareImportUrl?: string;
    repoMarkdownImport?: boolean;
    repoMarkdownImportUrl?: string;
    ref?: Ref<EditorWorkspaceHandle>;
  }>
>;

let editorWorkspacePromise: Promise<EditorWorkspaceComponent> | null = null;

function loadEditorWorkspace() {
  editorWorkspacePromise ??= import("../editor-workspace").then(
    (mod) => mod.EditorWorkspace,
  );
  return editorWorkspacePromise;
}

function ConverterSkeleton({
  observeRef,
  className,
}: Readonly<{
  observeRef?: Ref<HTMLDivElement>;
  className?: string;
}>) {
  return (
    <div
      ref={observeRef}
      data-app-shell-mode="embedded"
      data-converter-deferred=""
      className={classNames(embeddedConverterShellClassName, className)}
      aria-busy="true"
      aria-label="Loading Markdown converter"
    >
      <div className="flex min-h-0 flex-1 flex-col bg-white">
        <div className="shrink-0 border-b border-zinc-200 bg-white px-3 py-2 md:hidden">
          <div className="grid grid-cols-2 rounded-md border border-zinc-200 bg-zinc-100 p-1">
            <div className="h-8 rounded-md bg-white" />
            <div className="h-8 rounded-md bg-transparent" />
          </div>
        </div>
        <div className="relative z-20 flex h-14 shrink-0 items-center gap-2 border-b border-zinc-200/80 bg-[var(--surface-toolbar)] sm:gap-3">
          <div className="min-w-0 flex-1 px-2">
            <div className="h-8 max-w-md rounded-md bg-zinc-100" />
          </div>
          <div className="flex shrink-0 items-center gap-1 py-2 pl-1 pr-2 sm:gap-2 sm:px-3">
            <div className="h-10 w-36 rounded-md bg-zinc-100" />
          </div>
        </div>
        <div className="flex min-h-0 flex-1">
          <div className="min-h-0 flex-1 border-r border-zinc-200 bg-zinc-50 p-4">
            <div className="space-y-2.5">
              <div className="h-3 w-3/4 rounded bg-zinc-200/90" />
              <div className="h-3 w-full rounded bg-zinc-200/70" />
              <div className="h-3 w-5/6 rounded bg-zinc-200/70" />
              <div className="h-3 w-2/3 rounded bg-zinc-200/60" />
              <div className="mt-4 h-3 w-1/2 rounded bg-zinc-200/50" />
              <div className="h-3 w-4/5 rounded bg-zinc-200/50" />
            </div>
          </div>
          <div
            className="hidden min-h-0 flex-1 md:block"
            style={{ backgroundColor: defaultPreviewSurfaceBackground }}
          />
        </div>
        <div className="status-bar grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-3 border-t px-3 py-1.5 text-xs">
          <span className="h-3 w-36 rounded bg-zinc-200/70" aria-hidden="true" />
          <span className="justify-self-center" aria-hidden="true" />
          <span className="h-3 w-14 justify-self-end rounded bg-zinc-200/70" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

function shouldMountConverterImmediately() {
  if (typeof window === "undefined") {
    return false;
  }
  if (typeof IntersectionObserver === "undefined") {
    return true;
  }
  return window.location.hash === "#converter";
}

export function DeferredEmbeddedConverter({
  sampleId = "home",
  editorSource = "home",
  workspaceRef,
  onWorkspaceReadyChange,
  shellClassName,
  chatShareProvider,
  chatShareImportUrl,
  repoMarkdownImport = false,
  repoMarkdownImportUrl,
}: Readonly<{
  /** Seed document id — resolved client-side so bodies stay out of RSC HTML. */
  sampleId?: SamplePreviewId;
  editorSource?: EditorPreferencesSource;
  workspaceRef?: RefObject<EditorWorkspaceHandle | null>;
  onWorkspaceReadyChange?: (ready: boolean) => void;
  /** Override or extend the default embedded converter shell classes. */
  shellClassName?: string;
  chatShareProvider?: ChatShareProvider;
  chatShareImportUrl?: string;
  repoMarkdownImport?: boolean;
  repoMarkdownImportUrl?: string;
}> = {}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [shouldMount, setShouldMount] = useState(
    shouldMountConverterImmediately,
  );
  const [Workspace, setWorkspace] = useState<EditorWorkspaceComponent | null>(
    null,
  );
  const sample = getEmbeddedSample(sampleId);

  // Prefetch the editor chunk as soon as this section mounts so near-viewport
  // mounts (including above-the-fold) do not sit on an empty shell.
  useEffect(() => {
    let cancelled = false;
    void loadEditorWorkspace().then((component) => {
      if (!cancelled) {
        setWorkspace(() => component);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (shouldMount) {
      return;
    }

    let cancelled = false;
    const mount = () => {
      if (!cancelled) {
        setShouldMount(true);
      }
    };

    if (shouldMountConverterImmediately()) {
      const idle =
        typeof requestAnimationFrame === "function"
          ? requestAnimationFrame(() => mount())
          : window.setTimeout(mount, 0);
      return () => {
        cancelled = true;
        if (typeof requestAnimationFrame === "function") {
          cancelAnimationFrame(idle as number);
        } else {
          window.clearTimeout(idle as number);
        }
      };
    }

    const node = rootRef.current;
    if (!node) {
      return;
    }

    const onHashChange = () => {
      if (window.location.hash === "#converter") {
        mount();
      }
    };

    window.addEventListener("hashchange", onHashChange);

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          mount();
        }
      },
      { root: null, rootMargin: converterRootMargin, threshold: 0 },
    );
    observer.observe(node);

    return () => {
      cancelled = true;
      window.removeEventListener("hashchange", onHashChange);
      observer.disconnect();
    };
  }, [shouldMount]);

  const workspaceReady = Boolean(shouldMount && Workspace);

  useEffect(() => {
    onWorkspaceReadyChange?.(workspaceReady);
  }, [onWorkspaceReadyChange, workspaceReady]);

  if (!shouldMount || !Workspace) {
    return (
      <ConverterSkeleton observeRef={rootRef} className={shellClassName} />
    );
  }

  return (
    <div
      data-app-shell-mode="embedded"
      className={classNames(embeddedConverterShellClassName, shellClassName)}
    >
      <Workspace
        ref={workspaceRef}
        variant="embedded"
        editorSource={editorSource}
        initialMarkdown={sample.markdown}
        initialFilename={sample.filename}
        chatShareProvider={chatShareProvider}
        chatShareImportUrl={chatShareImportUrl}
        repoMarkdownImport={repoMarkdownImport}
        repoMarkdownImportUrl={repoMarkdownImportUrl}
      />
    </div>
  );
}
